import styled, { css } from 'styled-components';
import { ThemeColours } from '../../../styles/ThemeColours';
import { FONT_STACK, FontWeight } from '../../../styles/constants';
import { propSwitch } from '../../../util/propSwitch';
import { Asterisk } from '../../icons/outlined/Asterisk';
import { Close } from '../../icons/outlined/Close';
import { Tick } from '../../icons/outlined/Tick';

export interface InputProps {
  iconVisible?: boolean;
  labelVisible?: boolean;
  error?: boolean;
}

export const inputStyles = css<InputProps>`
  ${FONT_STACK};
  border: 1px solid ${propSwitch('error', ThemeColours.negative, ThemeColours.mono200)};
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 16px;
  line-height: 24px;
  outline: none;
  padding: ${propSwitch('labelVisible', '21px 16px 8px', '12px 16px')};
  padding-inline-end: 48px;
  width: 100%;

  &:hover:not([disabled]) {
    border-color: ${propSwitch('error', ThemeColours.negative, ThemeColours.primary)};
  }

  &:focus:not([disabled]) {
    border-color: ${ThemeColours.primary};
  }

  &[disabled] {
    background-color: ${ThemeColours.mono100};
  }

  &:not(:focus):not([disabled])::placeholder {
    color: ${propSwitch('error', ThemeColours.negative, ThemeColours.mono600)};
  }
`;

export const Input = styled.input<InputProps>`
  ${inputStyles}
`;

export const Wrapper = styled.div`
  overflow: hidden;
  position: relative;
  white-space: nowrap;
  width: 100%;
`;

export interface LabelProps {
  hasValue: boolean;
  error?: boolean;
}

export const Label = styled.label<LabelProps>`
  ${FONT_STACK};
  color: ${({ error, hasValue }) =>
    error ? ThemeColours.negative : hasValue ? ThemeColours.mono700 : ThemeColours.mono600};
  font-size: ${propSwitch('hasValue', '12px', '16px')};
  font-weight: ${propSwitch('hasValue', FontWeight.SEMI_BOLD, FontWeight.REGULAR)};
  left: 16px;
  line-height: ${propSwitch('hasValue', '14px', '24px')};
  pointer-events: none;
  position: absolute;
  top: ${propSwitch('hasValue', '7px', 'calc(50% - 12px)')};
  transition-duration: 0.3s;
  transition-property: color, font-size, line-height, font-weight, top;
  user-select: none;

  ${Input}:hover ~ & {
    color: ${({ error, hasValue }) =>
      error ? ThemeColours.negative : hasValue ? ThemeColours.primary : ThemeColours.mono600};
  }

  ${Input}:focus ~ & {
    color: ${ThemeColours.primary};
    font-size: 12px;
    font-weight: ${FontWeight.SEMI_BOLD};
    line-height: 14px;
    top: 7px;
  }

  ${Input}[disabled] ~ & {
    color: ${ThemeColours.mono500};
  }
`;

export const AsteriskIcon = styled(Asterisk)`
  color: ${ThemeColours.negative};
  height: 24px;
  pointer-events: none;
  position: absolute;
  right: 16px;
  top: calc(50% - 12px);
  width: 24px;
`;

export const TickIcon = styled(Tick)`
  color: ${ThemeColours.accent};
  height: 24px;
  pointer-events: none;
  position: absolute;
  right: 13px;
  top: calc(50% - 12px);
  width: 24px;
`;

export const CloseIcon = styled(Close)`
  color: ${ThemeColours.negative};
  height: 24px;
  pointer-events: none;
  position: absolute;
  right: 18px;
  top: calc(50% - 12px);
  width: 24px;
`;
